import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Clock, Pause, Play, Send, Loader2, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';

const fmtTime = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

// Render passage text where blanks are `___n___` or `___n:wrong___` (editing).
// Replaces each blank with an inline input, wired to answers[qid].
const PassageWithBlanks = ({ passage, questions, answers, onChange, disabled, showIncorrect }) => {
  if (!passage) return null;
  const parts = [];
  let last = 0;
  const rx = /___(\d+)(?::([^_]+))?___/g;
  let m;
  while ((m = rx.exec(passage)) !== null) {
    const n = parseInt(m[1], 10);
    const wrongWord = m[2];
    parts.push({ kind: 'text', text: passage.slice(last, m.index) });
    parts.push({ kind: 'blank', n, wrongWord });
    last = m.index + m[0].length;
  }
  parts.push({ kind: 'text', text: passage.slice(last) });
  return (
    <div className="text-slate-800 leading-8 whitespace-pre-wrap">
      {parts.map((p, i) => {
        if (p.kind === 'text') return <span key={i}>{p.text}</span>;
        const q = questions.find((x) => x.n === p.n);
        if (!q) return <span key={i} className="font-mono text-slate-400">[{p.n}]</span>;
        return (
          <span key={i} className="inline-flex items-baseline gap-1 mx-1">
            <span className="text-[10px] font-black text-emerald-700">{p.n}.</span>
            {showIncorrect && p.wrongWord && (
              <span className="line-through text-rose-500 text-sm mr-1">{p.wrongWord}</span>
            )}
            <input
              type="text"
              disabled={disabled}
              value={answers[q.id] || ''}
              onChange={(e) => onChange(q.id, e.target.value)}
              className="border-b-2 border-emerald-400 focus:border-emerald-600 focus:outline-none px-1 min-w-[80px] max-w-[160px] bg-emerald-50/50 text-slate-900 font-semibold"
              data-testid={`wa-blank-${p.n}`}
            />
          </span>
        );
      })}
    </div>
  );
};

const WeightedAssessmentRunnerPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wa, setWa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [pausedRemaining, setPausedRemaining] = useState(null); // when set → paused
  const [now, setNow] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const saveTimer = useRef(null);
  const dirtyRef = useRef(false);

  // Load
  useEffect(() => {
    (async () => {
      try {
        const data = await api.waGet(id);
        setWa(data);
        setAnswers(data.answers || {});
        if (data.status === 'submitted') {
          navigate(`/weighted-assessments/${id}/results`, { replace: true });
        }
      } catch (e) {
        toast.error('Assessment not found');
        navigate('/weighted-assessments');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  // Timer tick
  useEffect(() => {
    if (!wa || wa.status === 'submitted') return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [wa]);

  // Auto-save every 15 seconds when dirty
  const flushSave = useCallback(async () => {
    if (!dirtyRef.current || !wa) return;
    dirtyRef.current = false;
    try {
      await api.waSave(wa.id, answers);
    } catch (e) {
      // silent — retry on next tick
      dirtyRef.current = true;
    }
  }, [wa, answers]);

  useEffect(() => {
    if (saveTimer.current) clearInterval(saveTimer.current);
    saveTimer.current = setInterval(flushSave, 15000);
    return () => clearInterval(saveTimer.current);
  }, [flushSave]);

  const remainingSecs = useMemo(() => {
    if (!wa) return 0;
    if (pausedRemaining !== null) return pausedRemaining;
    const started = new Date(wa.started_at).getTime();
    const elapsed = (now - started) / 1000;
    return Math.max(0, (wa.duration_seconds || 6600) - elapsed);
  }, [wa, now, pausedRemaining]);

  // Auto-submit when time hits 0
  useEffect(() => {
    if (wa && wa.status !== 'submitted' && pausedRemaining === null && remainingSecs <= 0 && !submitting) {
      handleSubmit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingSecs]);

  const onChangeAnswer = (qid, val) => {
    setAnswers((prev) => ({ ...prev, [qid]: val }));
    dirtyRef.current = true;
  };

  const togglePause = () => {
    if (pausedRemaining === null) {
      setPausedRemaining(remainingSecs);
      toast.info('Paused — timer stopped. Click Resume when ready.');
    } else {
      // shift started_at forward by however long we paused
      const newStart = new Date(Date.now() - ((wa.duration_seconds - pausedRemaining) * 1000)).toISOString();
      setWa((w) => ({ ...w, started_at: newStart }));
      setPausedRemaining(null);
      toast.success('Resumed');
    }
  };

  const handleSubmit = async (auto = false) => {
    if (submitting) return;
    if (!auto && !window.confirm('Submit your Weighted Assessment now? You will not be able to change your answers.')) return;
    setSubmitting(true);
    const t = toast.loading(auto ? 'Time is up — submitting…' : 'Submitting & grading (may take up to a minute)…');
    try {
      await flushSave();
      await api.waSubmit(id, answers);
      toast.success('Submitted!', { id: t });
      navigate(`/weighted-assessments/${id}/results`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed', { id: t });
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-emerald-600" size={32}/></div>;
  if (!wa) return null;

  const paper = wa.paper;
  const totalAnswered = Object.values(answers).filter((v) => v && String(v).trim()).length;
  const totalItems = paper.sections.reduce((acc, sec) => acc + sec.topics.reduce((a, t) => a + (t.questions?.length || 0), 0), 0);

  return (
    <div className="min-h-screen bg-slate-50 pb-32">
      {/* Sticky header with timer */}
      <div className="sticky top-0 z-30 bg-white border-b-2 border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs font-black uppercase tracking-wide text-rose-700">P6 Weighted Assessment</div>
            <div className="text-xs text-slate-500">{totalAnswered}/{totalItems} answered</div>
          </div>
          <div className={`text-2xl font-black tabular-nums ${remainingSecs < 300 ? 'text-rose-600 animate-pulse' : 'text-emerald-700'}`} data-testid="wa-timer">
            <Clock className="inline mr-2" size={20}/>{fmtTime(remainingSecs)}
          </div>
          <div className="flex gap-2">
            <Button onClick={togglePause} variant="outline" size="sm" className="rounded-full" data-testid="wa-pause-btn">
              {pausedRemaining !== null ? <><Play size={14} className="mr-1"/>Resume</> : <><Pause size={14} className="mr-1"/>Pause</>}
            </Button>
            <Button onClick={() => handleSubmit(false)} disabled={submitting} className="bg-rose-600 hover:bg-rose-700 text-white rounded-full font-bold" data-testid="wa-submit-btn">
              {submitting ? <Loader2 className="animate-spin" size={14}/> : <><Send size={14} className="mr-1"/>Submit</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {paper.sections.map((section) => (
          <div key={section.section} className="mb-8">
            <div className="mb-3">
              <div className="text-[11px] font-black uppercase tracking-wider text-emerald-700">Section {section.section} · {section.marks} marks</div>
              <h2 className="text-2xl font-heading font-black text-slate-900">{section.label}</h2>
            </div>

            {section.topics.map((topic) => (
              <Card key={topic.topic} className="p-5 md:p-6 mb-4 rounded-2xl border-2 border-slate-200">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                  <h3 className="font-heading font-black text-slate-900">{topic.label}</h3>
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">{topic.marks} marks</span>
                </div>

                {/* Visual Text — poster + MCQs */}
                {topic.topic === 'visual_text' && topic.poster && (
                  <div className="mb-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-5">
                    <div className="text-xl font-heading font-black text-amber-900 mb-1">{topic.poster.title}</div>
                    {topic.poster.subtitle && <div className="text-sm text-amber-800 italic mb-3">{topic.poster.subtitle}</div>}
                    <div className="grid sm:grid-cols-2 gap-2 mb-3">
                      {(topic.poster.sections || []).map((s, i) => (
                        <div key={i} className="bg-white rounded-lg border border-amber-200 p-3">
                          <div className="text-[10px] font-black uppercase text-amber-700 tracking-wider mb-0.5">{s.heading}</div>
                          <div className="text-sm text-slate-800">{s.content}</div>
                        </div>
                      ))}
                    </div>
                    {topic.poster.footer && <div className="text-xs text-amber-800 italic border-t border-amber-200 pt-2">{topic.poster.footer}</div>}
                  </div>
                )}

                {/* Passage-based topics — Comp Cloze + Editing */}
                {(topic.topic === 'comprehension_cloze' || topic.topic === 'editing') && topic.passage && (
                  <PassageWithBlanks
                    passage={topic.passage}
                    questions={topic.questions}
                    answers={answers}
                    onChange={onChangeAnswer}
                    disabled={submitting}
                    showIncorrect={topic.topic === 'editing'}
                  />
                )}

                {/* Comp Open — passage on top, questions below */}
                {topic.topic === 'comprehension_open' && (
                  <>
                    {topic.passage && (
                      <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-slate-800 whitespace-pre-wrap leading-7 text-[15px]">
                        {topic.passage}
                      </div>
                    )}
                    <div className="space-y-4">
                      {topic.questions.map((q) => (
                        <div key={q.id}>
                          <div className="flex items-start gap-2 mb-1">
                            <span className="font-black text-emerald-700 text-sm">{q.n}.</span>
                            <span className="font-semibold text-slate-800 flex-1">{q.stem}</span>
                            <span className="text-[10px] font-black text-slate-500">[{q.marks} m]</span>
                          </div>
                          <Textarea
                            value={answers[q.id] || ''}
                            onChange={(e) => onChangeAnswer(q.id, e.target.value)}
                            placeholder="Type your answer here…"
                            className="w-full border-2 border-slate-200 rounded-xl text-sm min-h-[70px]"
                            disabled={submitting}
                            data-testid={`wa-open-${q.n}`}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* MCQ-style topics — Vocab MCQ + Visual Text questions */}
                {(topic.topic === 'vocabulary_mcq' || topic.topic === 'visual_text') && (
                  <div className="space-y-4">
                    {topic.questions.map((q, idx) => (
                      <div key={q.id}>
                        <div className="flex items-start gap-2 mb-2">
                          <span className="font-black text-emerald-700 text-sm">{idx + 1}.</span>
                          <span className="font-semibold text-slate-800 flex-1">{q.stem}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-6">
                          {q.options.map((opt, i) => {
                            const letter = ['A', 'B', 'C', 'D'][i];
                            const picked = answers[q.id] === opt;
                            return (
                              <button
                                key={i}
                                onClick={() => onChangeAnswer(q.id, opt)}
                                disabled={submitting}
                                className={`text-left px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                                  picked ? 'border-emerald-500 bg-emerald-50 text-emerald-900' : 'border-slate-200 bg-white hover:border-emerald-300'
                                }`}
                                data-testid={`wa-mcq-${q.id}-${letter}`}
                              >
                                <span className="font-black mr-2">{letter}.</span>{opt}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Vocab Cloze — sentence with inline blank */}
                {topic.topic === 'vocabulary_cloze' && (
                  <div className="space-y-3">
                    {topic.questions.map((q, idx) => (
                      <div key={q.id} className="flex items-baseline gap-2">
                        <span className="font-black text-emerald-700 text-sm">{idx + 1}.</span>
                        <span className="text-slate-800 leading-7">
                          {q.stem.split('____').map((part, i, arr) => (
                            <React.Fragment key={i}>
                              {part}
                              {i < arr.length - 1 && (
                                <Input
                                  className="inline-block w-32 mx-1 border-b-2 border-emerald-400 rounded-none bg-emerald-50/50 text-slate-900 font-semibold"
                                  value={answers[q.id] || ''}
                                  onChange={(e) => onChangeAnswer(q.id, e.target.value)}
                                  disabled={submitting}
                                  data-testid={`wa-vcloze-${idx + 1}`}
                                />
                              )}
                            </React.Fragment>
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        ))}

        <div className="text-center py-6">
          <Button onClick={() => handleSubmit(false)} disabled={submitting} className="bg-rose-600 hover:bg-rose-700 text-white font-black rounded-2xl px-8 py-6 text-base" data-testid="wa-submit-btn-bottom">
            {submitting ? <><Loader2 className="animate-spin mr-2" size={18}/>Submitting…</> : <><Send size={18} className="mr-2"/>Submit Weighted Assessment</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WeightedAssessmentRunnerPage;
